import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import QrCodeIcon from '@mui/icons-material/QrCode';
import RefreshIcon from '@mui/icons-material/Refresh';
import Input from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { EXECUTIVE_SESSION_TYPE } from '../Constants';
import { useLongPress } from '../useLongPress';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

export interface Survey {
  i: string | number;
  t: string;
  owner?: string;
  /** 'executive_session' on surveys created through that flow; absent otherwise. */
  surveyType?: string;
  participantCount?: number;
  /** ISO timestamp of the last invite send; absent until the invites go out. */
  invitesSentAt?: string;
}

interface TableSurveysRowProps {
  row: Survey;
  analyzeSurvey: (surveyId: string | number) => void;
  loading: string | number;
  getParticipants: (surveyId: string | number) => void;
  participantsBySurvey: { [key: string]: any };
  setQrModalOpen: (link: string) => void;
  loadingParticipants: string | number;
  selectedSurveys: Survey[];
  handleSelect: (survey: Survey) => void;
  isMobile: boolean;
  editLeaderReportDate: (
    survey: Survey,
    leader: string,
    reportDate: Dayjs | null,
    newReminderDate: Dayjs | null
  ) => void;
  getLeaderAndReportDate: (surveyId: string | number) => void;
  loadingLeader: string | number;
  leaderBySurvey: { [key: string]: any };
  onRemove?: () => void;
  showOwner?: boolean;
  /** Absent in contexts that cannot send invites (e.g. the teams tab). */
  sendExecutiveInvites?: (
    surveyId: string | number,
    surveyTitle: string
  ) => Promise<{ sent: number; failed: string[]; invitesSentAt?: string }>;
}

const toDayjs = (v: string | null | undefined): Dayjs | null => {
  if (!v) return null;
  return dayjs(v, 'YYYY-MM-DD');
};

export const TableSurveysRow: React.FC<TableSurveysRowProps> = ({
  row,
  analyzeSurvey,
  loading,
  getParticipants,
  participantsBySurvey,
  setQrModalOpen,
  loadingParticipants,
  selectedSurveys,
  handleSelect,
  isMobile,
  editLeaderReportDate,
  getLeaderAndReportDate,
  loadingLeader,
  leaderBySurvey,
  onRemove,
  showOwner,
  sendExecutiveInvites,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [invitesDialogOpen, setInvitesDialogOpen] = React.useState(false);
  const [invitesLoading, setInvitesLoading] = React.useState(false);
  const isExecutiveSession = row.surveyType === EXECUTIVE_SESSION_TYPE;

  const handleSendInvites = async () => {
    if (!sendExecutiveInvites) return;
    setInvitesLoading(true);
    try {
      const result = await sendExecutiveInvites(row.i, row.t);
      setInvitesDialogOpen(false);
      alert(
        t('surveys.invites_sent', { total: result.sent.toString() }) +
          (result.failed.length > 0
            ? '\n' + t('surveys.invites_failed', { list: result.failed.join(', ') })
            : '')
      );
    } catch (error) {
      console.error('An error occurred:', error);
      alert(t('surveys.invites_error'));
    } finally {
      setInvitesLoading(false);
    }
  };
  const [newLeader, setNewLeader] = React.useState<string>('');
  const [newReportDate, setNewReportDate] = React.useState<Dayjs | null>(null);
  const [newReminderDate, setNewReminderDate] = React.useState<Dayjs | null>(null);
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = React.useState<boolean>(false);
  const selected = selectedSurveys.map((s) => s.i.toString()).includes(row.i.toString());
  const openRow = (openState: boolean) => {
    if (openState) {
      getParticipants(row.i);
      getLeaderAndReportDate(row.i);
    }
    setOpen(openState);
  };
  const longPress = useLongPress(() => {
    handleSelect(row);
  });
  const existing = leaderBySurvey[row.i.toString()] ?? {};

  const resetEditFieldsFromExisting = React.useCallback(() => {
    setNewLeader(existing.leader ?? '');
    setNewReportDate(toDayjs(existing.reportDate));
    setNewReminderDate(toDayjs(existing.reminderDate));
  }, [existing.leader, existing.reportDate, existing.reminderDate]);

  const setEditWrapper = (flag: boolean) => {
    resetEditFieldsFromExisting();
    setEditMode(flag);
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{ '& > *': { borderBottom: 'unset' } }}
        hover
        selected={selected}
        {...longPress}
      >
        <TableCell>
          {!isMobile && !onRemove && (
            <Checkbox color="primary" checked={selected} onChange={() => handleSelect(row)} />
          )}
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              openRow(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell scope="row">{row.t}</TableCell>
        {showOwner && <TableCell>{row.owner}</TableCell>}
        <TableCell>
          <Button
            loading={loading.toString() === row.i.toString()}
            variant="outlined"
            onClick={() => {
              analyzeSurvey(row.i);
            }}
          >
            {t('surveys.analyze_responses')}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                m: 1,
                maxWidth: '100%',
                overflowX: 'hidden',
                paddingBottom: '16px',
                paddingTop: '16px',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'left', sm: 'left', md: 'center' }}
                flexWrap="wrap"
                sx={{ '& > *': { minWidth: 0 } }}
              >
                {/* Copy link */}
                {!editMode && (
                  <Box sx={{ flex: '0 0 auto' }}>
                    <Stack direction={'row'} spacing={2} alignItems={'center'}>
                      <Tooltip
                        onClose={() => setTooltipOpen(true)}
                        open={tooltipOpen}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={t('surveys.link_copied')}
                        placement="top"
                        slotProps={{ popper: { disablePortal: true } }}
                      >
                        <Button
                          variant="text"
                          onClick={() => {
                            setTooltipOpen(true);
                            navigator.clipboard.writeText('https://mgmtlab.limequery.com/' + row.i);
                            setTimeout(() => setTooltipOpen(false), 2000);
                          }}
                        >
                          {row.i}
                        </Button>
                      </Tooltip>
                      <IconButton
                        color="primary"
                        onClick={() => setQrModalOpen('https://mgmtlab.limequery.com/' + row.i)}
                      >
                        <QrCodeIcon />
                      </IconButton>
                      {isExecutiveSession && sendExecutiveInvites && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<MailOutlineIcon />}
                          disabled={!row.participantCount}
                          onClick={() => setInvitesDialogOpen(true)}
                        >
                          {t('surveys.send_invites')}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Participants + refresh */}
                {!editMode && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {loadingParticipants.toString() !== row.i.toString() &&
                        participantsBySurvey[row.i.toString()] &&
                        t('surveys.participants', {
                          count: participantsBySurvey[row.i.toString()]['completed_responses'],
                        })}
                    </Box>

                    <IconButton
                      loading={loadingParticipants.toString() === row.i.toString()}
                      color="primary"
                      onClick={() => getParticipants(row.i)}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Stack>
                )}

                {/* Leader (only if editMode OR leader exists) */}
                {(editMode || !!leaderBySurvey[row.i.toString()]?.leader) && (
                  <Box sx={{ flex: '1 1' }}>
                    {editMode ? (
                      <FormControl style={{ width: '100%' }}>
                        <Input
                          id="leader"
                          value={newLeader}
                          placeholder={t('surveys.new_leader_email')}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setNewLeader(e.target.value)
                          }
                        />
                        <FormHelperText>{t('surveys.new_leader_email_hint')}</FormHelperText>
                      </FormControl>
                    ) : (
                      <Box sx={{ wordBreak: 'break-word' }}>
                        {t('surveys.leader_label', {
                          leader: leaderBySurvey[row.i.toString()]!.leader,
                        })}
                      </Box>
                    )}
                  </Box>
                )}
                {(editMode || !!leaderBySurvey[row.i.toString()]?.reminderDate) && (
                  <Box sx={{ flex: '1 1' }}>
                    {editMode ? (
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                        <DatePicker
                          label={t('surveys.reminder_date')}
                          value={newReminderDate}
                          onChange={(v) => setNewReminderDate(v)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </LocalizationProvider>
                    ) : (
                      <Box>
                        {t('surveys.reminder_date_label', {
                          date: leaderBySurvey[row.i.toString()]!.reminderDate,
                        })}
                      </Box>
                    )}
                  </Box>
                )}
                {/* Report date (only if editMode OR reportDate exists) */}
                {(editMode || !!leaderBySurvey[row.i.toString()]?.reportDate) && (
                  <Box sx={{ flex: '1 1' }}>
                    {editMode ? (
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                        <DatePicker
                          label={t('surveys.report_date')}
                          value={newReportDate}
                          onChange={(v) => setNewReportDate(v)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </LocalizationProvider>
                    ) : (
                      <Box>
                        {t('surveys.report_date_label', {
                          date: leaderBySurvey[row.i.toString()]!.reportDate,
                        })}
                      </Box>
                    )}
                  </Box>
                )}
                {/* Actions */}
                <Stack direction={{ xs: 'row', sm: 'row' }} spacing={1} sx={{ flex: '0 0 auto' }}>
                  {!editMode ? (
                    <Button
                      variant="outlined"
                      disabled={loadingLeader.toString() === row.i.toString()}
                      onClick={() => setEditWrapper(true)}
                    >
                      {t('surveys.edit_leader_report')}
                    </Button>
                  ) : (
                    <>
                      <Button variant="text" color="inherit" onClick={() => setEditWrapper(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          editLeaderReportDate(row, newLeader, newReportDate, newReminderDate);
                          setEditWrapper(false);
                        }}
                      >
                        {t('common.confirm')}
                      </Button>
                    </>
                  )}
                </Stack>
                {onRemove && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={onRemove}
                    sx={{ flex: '0 0 auto', marginLeft: 'auto' }}
                  >
                    {t('common.remove')}
                  </Button>
                )}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Executive Session invites — confirmed because a resend mails everyone again */}
      <Dialog open={invitesDialogOpen} onClose={() => setInvitesDialogOpen(false)}>
        <DialogTitle>{t('surveys.send_invites_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('surveys.send_invites_body', { total: (row.participantCount ?? 0).toString() })}
          </Typography>
          {row.invitesSentAt && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
              {t('surveys.send_invites_resend', {
                date: dayjs(row.invitesSentAt).format('YYYY-MM-DD HH:mm'),
              })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvitesDialogOpen(false)} disabled={invitesLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSendInvites} disabled={invitesLoading} loading={invitesLoading}>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};
